import React, { useState, useEffect, useRef } from 'react';
import api from './api';
import {
  LoginContainer, LoginCard, LoginTitle, LoginSubtitle,
  LoginInput, LoginButton, LoginLink, ErrorText,
} from './ChatPage.styled';

// Country codes with flags and phone formats
const COUNTRIES = [
  { code: 'RU', name: 'Россия', dial: '+7', flag: '🇷🇺', format: '### ###-##-##' },
  { code: 'BY', name: 'Беларусь', dial: '+375', flag: '🇧🇾', format: '## ###-##-##' },
  { code: 'KZ', name: 'Казахстан', dial: '+7', flag: '🇰🇿', format: '### ###-##-##' },
  { code: 'UA', name: 'Украина', dial: '+380', flag: '🇺🇦', format: '## ###-##-##' },
  { code: 'US', name: 'США', dial: '+1', flag: '🇺🇸', format: '(###) ###-####' },
  { code: 'GB', name: 'Великобритания', dial: '+44', flag: '🇬🇧', format: '#### ### ###' },
  { code: 'DE', name: 'Германия', dial: '+49', flag: '🇩🇪', format: '### #######' },
  { code: 'FR', name: 'Франция', dial: '+33', flag: '🇫🇷', format: '# ## ## ## ##' },
  { code: 'IL', name: 'Израиль', dial: '+972', flag: '🇮🇱', format: '## ###-####' },
  { code: 'TR', name: 'Турция', dial: '+90', flag: '🇹🇷', format: '### ### ####' },
  { code: 'CN', name: 'Китай', dial: '+86', flag: '🇨🇳', format: '### #### ####' },
  { code: 'IN', name: 'Индия', dial: '+91', flag: '🇮🇳', format: '##### #####' },
  { code: 'JP', name: 'Япония', dial: '+81', flag: '🇯🇵', format: '### ### ####' },
  { code: 'KR', name: 'Южная Корея', dial: '+82', flag: '🇰🇷', format: '## #### ####' },
  { code: 'BR', name: 'Бразилия', dial: '+55', flag: '🇧🇷', format: '## #####-####' },
  { code: 'AE', name: 'ОАЭ', dial: '+971', flag: '🇦🇪', format: '## ### ####' },
  { code: 'GE', name: 'Грузия', dial: '+995', flag: '🇬🇪', format: '### ### ###' },
  { code: 'AM', name: 'Армения', dial: '+374', flag: '🇦🇲', format: '## ######' },
  { code: 'AZ', name: 'Азербайджан', dial: '+994', flag: '🇦🇿', format: '## ###-##-##' },
  { code: 'UZ', name: 'Узбекистан', dial: '+998', flag: '🇺🇿', format: '## ###-##-##' },
  { code: 'KG', name: 'Кыргызстан', dial: '+996', flag: '🇰🇬', format: '### ######' },
  { code: 'TJ', name: 'Таджикистан', dial: '+992', flag: '🇹🇯', format: '## ### ####' },
];

// Format digits according to template (# = digit, space/other = literal)
function applyFormat(digits, format) {
  let out = '';
  let di = 0;
  for (const ch of format) {
    if (ch === '#') {
      if (di >= digits.length) break;
      out += digits[di++];
    } else {
      if (di >= digits.length) break;
      out += ch;
    }
  }
  if (di < digits.length) out += digits.slice(di);
  return out;
}

export default function LoginPage({ onLogin }) {
  const [step, setStep] = useState(() => {
    const hash = (window.location.hash || '').toLowerCase();
    return hash.includes('register') ? 'register-phone' : 'phone';
  });
  // phone → password → (if register) name → account
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) setCountryOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const phoneDigits = phone.replace(/\D/g, '');
  const fullPhone = country.dial + phoneDigits;
  const canProceed = phoneDigits.length >= 7;

  const isRegister = step.startsWith('register');

  const handleLoginSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!password) { setError('Введите пароль'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { login: fullPhone, password });
      if (res.data.success) {
        localStorage.setItem('terka_token', res.data.token);
        localStorage.setItem('terka_refresh', res.data.refreshToken);
        onLogin(res.data.user);
      } else setError(res.data.message || 'Ошибка');
    } catch (err) {
      setError(err.response?.data?.message || 'Неверный телефон или пароль');
    } finally { setLoading(false); }
  };

  const handleRegisterSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!displayName.trim()) { setError('Введите имя'); return; }
    if (password.length < 6) { setError('Пароль минимум 6 символов'); return; }
    if (password !== passwordConfirm) { setError('Пароли не совпадают'); return; }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Неверный email'); return; }
    setLoading(true);
    try {
      const payload = {
        phone: fullPhone.replace(/\D/g, ''),
        password,
        displayName: displayName.trim(),
      };
      if (email.trim()) payload.email = email.trim().toLowerCase();
      const res = await api.post('/auth/register', payload);
      if (res.data.success) {
        localStorage.setItem('terka_token', res.data.token);
        localStorage.setItem('terka_refresh', res.data.refreshToken);
        onLogin(res.data.user);
      } else setError(res.data.message || 'Ошибка');
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось зарегистрироваться');
    } finally { setLoading(false); }
  };

  const goBack = () => {
    setError('');
    if (step === 'password' || step === 'register-info') setStep(isRegister ? 'register-phone' : 'phone');
  };

  const filteredCountries = COUNTRIES.filter(c => {
    if (!countrySearch.trim()) return true;
    const q = countrySearch.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q);
  });

  const CountryPicker = (
    <div ref={countryRef} style={{ position: 'relative', marginBottom: 12 }}>
      <button
        type="button"
        onClick={() => setCountryOpen(!countryOpen)}
        style={{
          width: '100%', padding: '14px 16px',
          border: '1px solid #e1e5e9', borderRadius: 10,
          background: 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 15, fontFamily: 'inherit',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>{country.flag}</span>
          <span>{country.name}</span>
        </span>
        <span style={{ color: '#999' }}>{countryOpen ? '▲' : '▼'}</span>
      </button>
      {countryOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'white', border: '1px solid #e1e5e9', borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          maxHeight: 300, overflowY: 'auto',
          zIndex: 10,
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid #eee' }}>
            <input
              autoFocus
              value={countrySearch}
              onChange={e => setCountrySearch(e.target.value)}
              placeholder="Поиск..."
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #e1e5e9', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          {filteredCountries.map(c => (
            <div
              key={c.code}
              onClick={() => { setCountry(c); setCountryOpen(false); setCountrySearch(''); setPhone(''); }}
              style={{
                padding: '10px 16px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                background: c.code === country.code ? '#f0f7ff' : 'white',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f8'}
              onMouseLeave={e => e.currentTarget.style.background = c.code === country.code ? '#f0f7ff' : 'white'}
            >
              <span style={{ fontSize: 22 }}>{c.flag}</span>
              <span style={{ flex: 1 }}>{c.name}</span>
              <span style={{ color: '#888', fontSize: 14 }}>{c.dial}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const PhoneField = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <div style={{
        padding: '14px 16px', background: '#f5f5f8',
        borderRadius: 10, fontSize: 15, color: '#333',
        fontWeight: 600, minWidth: 70, textAlign: 'center',
      }}>
        {country.dial}
      </div>
      <input
        type="tel"
        autoFocus
        value={applyFormat(phoneDigits, country.format)}
        onChange={e => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
          setPhone(digits);
        }}
        placeholder={country.format.replace(/#/g, '_')}
        style={{
          flex: 1, padding: '14px 16px',
          border: '1px solid #e1e5e9', borderRadius: 10,
          fontSize: 15, outline: 'none',
          fontFamily: 'inherit',
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && canProceed) {
            setStep(isRegister ? 'register-info' : 'password');
          }
        }}
      />
    </div>
  );

  return (
    <LoginContainer>
      <LoginCard>
        <LoginTitle>tërka</LoginTitle>

        {step === 'phone' && (
          <>
            <LoginSubtitle>Введите номер телефона</LoginSubtitle>
            {error && <ErrorText>{error}</ErrorText>}
            {CountryPicker}
            {PhoneField}
            <LoginButton
              type="button"
              disabled={!canProceed}
              onClick={() => setStep('password')}
            >
              Далее
            </LoginButton>
            <LoginLink onClick={() => { setStep('register-phone'); setError(''); window.location.hash = '#register'; }}>
              Нет аккаунта? Зарегистрироваться
            </LoginLink>
          </>
        )}

        {step === 'password' && (
          <form onSubmit={handleLoginSubmit}>
            <LoginSubtitle>{country.flag} {country.dial} {applyFormat(phoneDigits, country.format)}</LoginSubtitle>
            {error && <ErrorText>{error}</ErrorText>}
            <LoginInput
              type="password"
              autoFocus
              placeholder="Пароль"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <LoginButton type="submit" disabled={loading || !password}>
              {loading ? '...' : 'Войти'}
            </LoginButton>
            <LoginLink onClick={goBack}>← Изменить номер</LoginLink>
            <LoginLink onClick={async () => {
              if (!window.confirm('Отправить письмо для сброса пароля на email?')) return;
              const addr = prompt('Введите email:', '');
              if (!addr) return;
              try {
                await api.post('/auth/forgot-password', { email: addr.trim() });
                alert('Если этот email привязан к аккаунту — письмо отправлено');
              } catch { alert('Ошибка'); }
            }}>Забыли пароль?</LoginLink>
          </form>
        )}

        {step === 'register-phone' && (
          <>
            <LoginSubtitle>Введите номер телефона</LoginSubtitle>
            {error && <ErrorText>{error}</ErrorText>}
            {CountryPicker}
            {PhoneField}
            <LoginButton
              type="button"
              disabled={!canProceed}
              onClick={() => setStep('register-info')}
            >
              Далее
            </LoginButton>
            <LoginLink onClick={() => { setStep('phone'); setError(''); window.location.hash = ''; }}>
              Уже есть аккаунт? Войти
            </LoginLink>
          </>
        )}

        {step === 'register-info' && (
          <form onSubmit={handleRegisterSubmit}>
            <LoginSubtitle>{country.flag} {country.dial} {applyFormat(phoneDigits, country.format)}</LoginSubtitle>
            {error && <ErrorText>{error}</ErrorText>}
            <LoginInput
              autoFocus
              placeholder="Ваше имя"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              autoComplete="name"
            />
            <LoginInput
              type="email"
              placeholder="Email (необязательно)"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
            />
            <LoginInput
              type="password"
              placeholder="Придумайте пароль"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <LoginInput
              type="password"
              placeholder="Повторите пароль"
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              autoComplete="new-password"
            />
            <LoginButton type="submit" disabled={loading || !displayName.trim() || !password}>
              {loading ? '...' : 'Создать аккаунт'}
            </LoginButton>
            <LoginLink onClick={goBack}>← Изменить номер</LoginLink>
          </form>
        )}
      </LoginCard>
    </LoginContainer>
  );
}
